import { useState, useEffect, useCallback } from 'react';

export type MicrophonePermissionStatus = 'granted' | 'denied' | 'prompt' | 'checking' | 'unsupported';

interface MicrophonePermissionState {
  status: MicrophonePermissionStatus;
  isChecking: boolean;
  error: string | null;
  deviceId: string | null;
  devices: MediaDeviceInfo[];
}

export const useMicrophonePermission = () => {
  const [state, setState] = useState<MicrophonePermissionState>({
    status: 'checking',
    isChecking: true,
    error: null,
    deviceId: null,
    devices: [],
  });

  // Check if browser supports necessary APIs
  const checkBrowserSupport = useCallback(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setState(prev => ({
        ...prev,
        status: 'unsupported',
        isChecking: false,
        error: 'Your browser does not support microphone access. Please use a modern browser like Chrome, Firefox, or Edge.',
      }));
      return false;
    }
    return true;
  }, []);

  // Get list of available microphones
  const getAudioDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioDevices = devices.filter(device => device.kind === 'audioinput');
      
      // Set default device if available
      const defaultDevice = audioDevices.find(d => d.deviceId === 'default') || audioDevices[0];
      
      setState(prev => ({
        ...prev,
        devices: audioDevices,
        deviceId: defaultDevice?.deviceId || null,
      }));
      
      return audioDevices;
    } catch (error) {
      console.error('Error enumerating devices:', error);
      return [];
    }
  }, []);

  // Check current permission status
  const checkPermission = useCallback(async () => {
    if (!checkBrowserSupport()) return;

    setState(prev => ({ ...prev, isChecking: true, error: null }));

    try {
      // First, try to check permission without prompting
      if ('permissions' in navigator) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });

          setState(prev => ({
            ...prev,
            status: permissionStatus.state as MicrophonePermissionStatus,
            isChecking: false,
          }));

          // Listen for permission changes
          permissionStatus.addEventListener('change', () => {
            setState(prev => ({
              ...prev,
              status: permissionStatus.state as MicrophonePermissionStatus,
            }));
          });

          // If granted, get devices
          if (permissionStatus.state === 'granted') {
            await getAudioDevices();
          }

          return permissionStatus.state;
        } catch (error) {
          // Permissions API might not support microphone query in some browsers
        }
      }

      // If Permissions API not available, assume prompt state
      // DO NOT call getUserMedia here as it would trigger the permission prompt
      setState(prev => ({
        ...prev,
        status: 'prompt',
        isChecking: false,
      }));

      return 'prompt';

    } catch (error: any) {
      // Assume permission hasn't been granted yet (prompt state)
      setState(prev => ({
        ...prev,
        status: 'prompt',
        isChecking: false,
      }));
      return 'prompt';
    }
  }, [checkBrowserSupport, getAudioDevices]);

  // Request microphone permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!checkBrowserSupport()) return false;

    setState(prev => ({ ...prev, isChecking: true, error: null }));

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: 16000,
        }, 
        video: false 
      });
      
      // Stop the stream immediately - we just needed permission
      stream.getTracks().forEach(track => track.stop());
      
      await getAudioDevices();
      
      setState(prev => ({
        ...prev,
        status: 'granted',
        isChecking: false,
        error: null,
      }));
      
      return true;
      
    } catch (error: any) {
      let errorMessage = 'Failed to access microphone.';
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage = 'Microphone permission denied. Please allow microphone access and try again.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Microphone is already in use by another application.';
      }
      
      setState(prev => ({
        ...prev,
        status: 'denied',
        isChecking: false,
        error: errorMessage,
      }));
      
      return false;
    }
  }, [checkBrowserSupport, getAudioDevices]);

  // Select a specific microphone device
  const selectDevice = useCallback((deviceId: string) => {
    setState(prev => ({
      ...prev,
      deviceId,
    }));
  }, []);

  // Open browser settings for microphone permissions
  const openSettings = useCallback(() => {
    // Provide browser-specific instructions
    const isChrome = /Chrome/.test(navigator.userAgent);
    const isFirefox = /Firefox/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    let instructions = '';
    
    if (isChrome) {
      instructions = 'Click the lock icon in the address bar, then select "Site settings" and allow microphone access.';
    } else if (isFirefox) {
      instructions = 'Click the lock icon in the address bar, then click "Connection secure" > "More information" > "Permissions" and allow microphone access.';
    } else if (isSafari) {
      instructions = 'Go to Safari > Preferences > Websites > Microphone and allow access for this website.';
    } else {
      instructions = 'Check your browser settings to allow microphone access for this website.';
    }
    
    setState(prev => ({
      ...prev,
      error: instructions,
    }));
  }, []);

  // Check permission on mount
  useEffect(() => {
    checkPermission();
  }, []);

  // Re-check when page gains focus (user might have changed settings)
  useEffect(() => {
    const handleFocus = () => {
      if (state.status === 'denied') {
        checkPermission();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [state.status, checkPermission]);

  return {
    status: state.status,
    isChecking: state.isChecking,
    error: state.error,
    deviceId: state.deviceId,
    devices: state.devices,
    requestPermission,
    checkPermission,
    selectDevice,
    openSettings,
    hasPermission: state.status === 'granted',
    needsPermission: state.status === 'prompt',
    isBlocked: state.status === 'denied',
    isUnsupported: state.status === 'unsupported',
  };
};